/**
 * HeroLeadsPanel — Admin view of home-hero lead captures.
 *
 * Every visitor who clicks "Get my LinkedIn makeover" on the home hero
 * hits POST /api/hero-lead with the LinkedIn URL + goal + plan intent
 * + region BEFORE the Razorpay checkout opens. Even abandoned checkouts
 * land here so ops can follow up with warm leads.
 *
 * Backend endpoint: GET /api/admin/hero-leads
 *   params: limit, plan_key, converted ('yes' | 'no')
 *   returns: { items: [{linkedin_url, goal, plan_key, currency, email,
 *                       ip, created_at, converted, conversion, user, ...}], count }
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, Search, ExternalLink, Users, Zap, ShieldCheck, CheckCircle2, XCircle, Copy, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { AdminDateFilter } from './AdminDateFilter';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { adminApi } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

const PLAN_LABEL = {
  one_time: 'AI Profile Makeover',
  expert_audit: 'Expert-Audited Profile',
};

const GOAL_LABEL = {
  recruiter_visibility: 'Recruiter visibility',
  business_development: 'Business dev.',
  thought_leadership: 'Thought leadership',
};

function StatChip({ icon: Icon, label, value, tone = 'slate' }) {
  return (
    <div className={`flex items-center gap-3 rounded-2xl border border-border bg-background px-4 py-3`}>
      <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl bg-${tone}-100 text-${tone}-700 dark:bg-${tone}-500/15 dark:text-${tone}-300`}>
        <Icon size={16} />
      </span>
      <div>
        <p className="text-[10.5px] font-mono-edge tracking-[0.18em] uppercase text-muted-foreground leading-none">{label}</p>
        <p className="mt-1 text-[22px] font-bold text-foreground tabular-nums leading-none">{value}</p>
      </div>
    </div>
  );
}

// Referral summary cell — surfaces external referrer (google.com,
// linkedin.com, etc.) + first landing path + any UTM tag. Falls back to
// "Direct / unknown" when the referrer is missing.
export function ReferralCell({ referral }) {
  if (!referral || typeof referral !== 'object') {
    return <span className="text-muted-foreground italic">Direct / unknown</span>;
  }
  const ext = (referral.external_referrer || '').trim();
  const landing = (referral.landing_path || '').trim();
  const utm = referral.utm_source || referral.utm_campaign || '';
  let host = '';
  if (ext) {
    try { host = new URL(ext).host.replace(/^www\./, ''); } catch (_e) { host = ext.slice(0, 40); }
  }
  return (
    <div className="flex flex-col gap-0.5" title={ext || landing || 'unknown'}>
      {host ? (
        <a
          href={ext}
          target="_blank"
          rel="noreferrer"
          className="text-sky-600 dark:text-sky-400 hover:underline truncate max-w-[200px]"
        >
          {host}
        </a>
      ) : (
        <span className="text-muted-foreground">Direct</span>
      )}
      {landing && landing !== '/' && (
        <span className="text-[10px] text-muted-foreground truncate max-w-[200px]">
          Landed: {landing}
        </span>
      )}
      {utm && (
        <span className="text-[10px] font-mono-edge uppercase tracking-[0.12em] text-primary/80 truncate max-w-[200px]">
          {utm}
        </span>
      )}
    </div>
  );
}

export function HeroLeadsPanel() {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [convFilter, setConvFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (planFilter !== 'all') params.plan_key = planFilter;
      if (convFilter !== 'all') params.converted = convFilter;
      if (dateRange.from) params.date_from = dateRange.from;
      if (dateRange.to) params.date_to = dateRange.to;
      const data = await adminApi.listHeroLeads(params);
      setRows(data?.items || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load hero leads'));
    } finally {
      setLoading(false);
    }
  }, [planFilter, convFilter, dateRange.from, dateRange.to]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => {
      const bag = [r.linkedin_url, r.email, r.goal, r.plan_key, r.currency, r.ip, r.user?.name, r.user?.email]
        .filter(Boolean).map((v) => String(v).toLowerCase()).join(' | ');
      return bag.includes(needle);
    });
  }, [rows, q]);

  const stats = useMemo(() => {
    const totalRows = rows.length;
    const converted = rows.filter((r) => r.converted).length;
    const expert = rows.filter((r) => r.plan_key === 'expert_audit').length;
    const ai = rows.filter((r) => r.plan_key === 'one_time').length;
    return { totalRows, converted, expert, ai };
  }, [rows]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied');
    } catch { /* ignore */ }
  };

  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = new URLSearchParams({ limit: '5000' });
      if (planFilter !== 'all') params.set('plan_key', planFilter);
      if (convFilter !== 'all') params.set('converted', convFilter);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/hero-leads/export.csv?${params}`;
      const token = localStorage.getItem('le_token');
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`Export failed (HTTP ${resp.status})`);
      const blob = await resp.blob();
      const dl = document.createElement('a');
      const filename = (resp.headers.get('content-disposition') || '').match(/filename="?([^"]+)"?/i)?.[1]
        || `blayzrr-hero-leads-${Date.now()}.csv`;
      dl.href = URL.createObjectURL(blob);
      dl.download = filename;
      document.body.appendChild(dl);
      dl.click();
      dl.remove();
      URL.revokeObjectURL(dl.href);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e?.message || 'CSV export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-hero-leads-panel">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatChip icon={Users} label="Total leads" value={stats.totalRows} tone="sky" />
        <StatChip icon={CheckCircle2} label="Converted" value={stats.converted} tone="emerald" />
        <StatChip icon={ShieldCheck} label="Expert Audit intent" value={stats.expert} tone="amber" />
        <StatChip icon={Zap} label="AI Makeover intent" value={stats.ai} tone="blue" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search LinkedIn URL, email, goal, IP…"
            className="pl-9 h-10"
            data-testid="hero-leads-search"
          />
        </div>
        <Select value={planFilter} onValueChange={setPlanFilter}>
          <SelectTrigger className="w-[200px] h-10" data-testid="hero-leads-plan-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plan intents</SelectItem>
            <SelectItem value="one_time">AI Profile Makeover</SelectItem>
            <SelectItem value="expert_audit">Expert-Audited Profile</SelectItem>
          </SelectContent>
        </Select>
        <Select value={convFilter} onValueChange={setConvFilter}>
          <SelectTrigger className="w-[180px] h-10" data-testid="hero-leads-conv-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All leads</SelectItem>
            <SelectItem value="yes">Converted only</SelectItem>
            <SelectItem value="no">Not yet converted</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} disabled={loading} className="h-10 gap-1.5" data-testid="hero-leads-refresh">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Refresh
        </Button>
        <Button
          variant="default"
          onClick={exportCsv}
          disabled={exporting || rows.length === 0}
          className="h-10 gap-1.5"
          data-testid="hero-leads-export-csv"
        >
          {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          Export CSV
        </Button>
      </div>

      <div className="mb-3">
        <AdminDateFilter value={dateRange} onChange={setDateRange} testId="hero-leads-date-filter" />
      </div>

      <div className="rounded-xl border border-border bg-background overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[240px]">LinkedIn URL</TableHead>
              <TableHead>Goal</TableHead>
              <TableHead>Plan intent</TableHead>
              <TableHead>Region</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Referral</TableHead>
              <TableHead>Captured</TableHead>
              <TableHead className="text-right">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                  <Loader2 size={16} className="inline animate-spin mr-2" /> Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground py-8" data-testid="hero-leads-empty">
                  No leads match the current filters yet.
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.map((r) => {
              const url = r.linkedin_url || '';
              const short = url.replace(/^https?:\/\//, '').replace(/\/$/, '');
              return (
                <TableRow key={r.id} data-testid={`hero-lead-row-${r.id}`}>
                  <TableCell className="align-top">
                    <a
                      href={url.startsWith('http') ? url : `https://${url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-sky-600 dark:text-sky-400 font-medium text-[13px] hover:underline"
                      title={url}
                    >
                      <span className="max-w-[220px] truncate">{short || '—'}</span>
                      <ExternalLink size={11} />
                    </a>
                    <button
                      type="button"
                      onClick={() => copy(url)}
                      className="ml-2 inline-flex items-center text-muted-foreground hover:text-foreground"
                      aria-label="Copy URL"
                    >
                      <Copy size={11} />
                    </button>
                  </TableCell>
                  <TableCell className="align-top text-[12.5px]">
                    {GOAL_LABEL[r.goal] || r.goal || <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="align-top">
                    <Badge variant={r.plan_key === 'expert_audit' ? 'default' : 'secondary'} className="text-[11px]">
                      {PLAN_LABEL[r.plan_key] || r.plan_key || '—'}
                    </Badge>
                  </TableCell>
                  <TableCell className="align-top text-[12.5px]">
                    <div className="flex flex-col leading-tight">
                      <span className="font-medium">{r.currency || '—'}</span>
                      <span className="text-muted-foreground text-[11px]">{r.ip || '—'}</span>
                    </div>
                  </TableCell>
                  <TableCell className="align-top text-[12.5px]">
                    {r.email
                      ? <span className="text-foreground">{r.email}</span>
                      : <span className="text-muted-foreground italic">not shared</span>}
                    {r.user?.name && (
                      <div className="text-[11px] text-muted-foreground">
                        {r.user.name}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="align-top text-[11.5px] max-w-[220px]">
                    <ReferralCell referral={r.referral} />
                  </TableCell>
                  <TableCell className="align-top text-[12.5px] text-muted-foreground">
                    {r.created_at ? format(new Date(r.created_at), 'd MMM · HH:mm') : '—'}
                  </TableCell>
                  <TableCell className="align-top text-right">
                    {r.converted ? (
                      <div className="inline-flex flex-col items-end gap-0.5">
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300 px-2 py-0.5 text-[11px] font-semibold">
                          <CheckCircle2 size={11} /> Converted
                        </span>
                        {r.conversion?.sku && (
                          <span className="text-[10.5px] text-muted-foreground">
                            {PLAN_LABEL[r.conversion.sku] || r.conversion.sku}
                            {r.conversion.amount_paise != null && (
                              <> · {r.conversion.currency === 'USD' ? '$' : '₹'}{(r.conversion.amount_paise / 100).toFixed(0)}</>
                            )}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-500/15 dark:text-slate-300 px-2 py-0.5 text-[11px] font-semibold">
                        <XCircle size={11} /> Pending
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <p className="text-[11.5px] text-muted-foreground">
        Showing {filtered.length} of {rows.length} lead{rows.length === 1 ? '' : 's'} captured. Conversion is joined on the buyer&apos;s email — cold visitors who paid under a different email than they haven&apos;t submitted here will not auto-link.
      </p>
    </div>
  );
}

export default HeroLeadsPanel;
